import React, { useId } from 'react';
import { GOODS_DESTINATION_LABELS, GoodsDestination } from '../../lib/creditNoteApi';

/**
 * D44 #7 (VR-4) — where an Item Return's goods went, asked once per note. The
 * note cannot be issued until it is answered (the server refuses with
 * CREDIT_NOTE_GOODS_DESTINATION_REQUIRED). Only "Back into United stock" moves
 * stock; the other two leave it as it is.
 *
 * Controlled: the page owns the value and saves it. Read-only past Draft through
 * the page's disabled fieldset.
 */
const OPTIONS: Array<{ value: GoodsDestination; hint: string }> = [
  { value: 'united_stock', hint: 'They go back into the batches they were shipped from. Say which below.' },
  { value: 'vendor', hint: 'The vessel sent them to the vendor. United stock does not change.' },
  { value: 'not_returned', hint: 'The goods did not come back. United stock does not change.' },
];

export const GoodsDestinationField: React.FC<{
  value: GoodsDestination | '';
  onChange: (next: GoodsDestination) => void;
  /** Unanswered on a draft: say that Issue needs it. */
  missing?: boolean;
}> = ({ value, onChange, missing = false }) => {
  const id = useId();
  return (
    <div>
      <div id={`${id}-label`} className="mb-1 text-xs font-medium text-slate-600">
        Where did the returned goods go? <span className="text-red-600">*</span>
      </div>
      <div role="radiogroup" aria-labelledby={`${id}-label`} className="grid grid-cols-1 gap-2 md:grid-cols-3">
        {OPTIONS.map((option) => {
          const checked = value === option.value;
          return (
            <label
              key={option.value}
              className={`flex items-start gap-2 rounded-md border px-3 py-2 ${
                checked ? 'border-primary-500 bg-primary-50' : 'border-slate-200 bg-white'
              }`}
            >
              <input
                type="radio"
                name={`${id}-goods-destination`}
                value={option.value}
                checked={checked}
                onChange={() => onChange(option.value)}
                className="mt-0.5 h-4 w-4 shrink-0"
              />
              <span>
                <span className="block text-xs font-medium text-slate-800">{GOODS_DESTINATION_LABELS[option.value]}</span>
                <span className="block text-xs text-slate-500">{option.hint}</span>
              </span>
            </label>
          );
        })}
      </div>
      {missing && (
        <div className="mt-1 text-xs text-amber-700">Answer this before issuing the note.</div>
      )}
    </div>
  );
};

export default GoodsDestinationField;
